import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {PageItemsObject} from '@PosterWhiteboard/page/page-items.class';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';

/**
 * Pure page-duration math, intentionally free of any Fabric/canvas imports so the timeline UI, the
 * Redux selectors, and unit tests can depend on it without loading the editor's heavy item pipeline.
 * The live `Page.getDuration()` reuses the same `combinePageDuration` formula, so the playback clock's
 * max and the timeline's max are always derived identically.
 */

/** Minimum playback duration, in seconds, given to a static (non-video) page in webpage playback. */
export const WEBPAGE_STATIC_PAGE_DURATION = 5;

/**
 * Combines a page's component spans into its effective duration. Audio and subtitles can each raise
 * the result above the visual span so playback runs long enough to play the audio out and reach
 * transcript items beyond the background. This is the single duration formula: both the Redux-side
 * `computePageDuration` and the live `Page.getDuration()` call it with the same three terms, so the
 * timeline's max and the playback clock's max can never drift apart.
 */
export const combinePageDuration = (backgroundVisualDuration: number, maxAudioEnd: number, maxSubtitleEnd: number): number => {
  return Math.max(backgroundVisualDuration, maxAudioEnd, maxSubtitleEnd);
};

/**
 * Computes the effective duration of a page from its stable child references.
 * When backgrounds exist, their accumulative duration governs (the long-running
 * UX rule). When no backgrounds exist, falls back to the max item end time. Audio
 * clips and subtitles can each raise the result above the base so playback runs
 * long enough to play the audio out and reach transcript items beyond the
 * background span. Empty pages return 0.
 *
 * This is the single source of truth for page duration: `getPageDuration` and the
 * `getCurrentPageDuration` selector both delegate here, and it mirrors
 * `Page.getDuration()` (both combine their terms via `combinePageDuration`).
 */
export const computePageDuration = (items: PageItemsObject, audioClips: AudioClipsObject | undefined): number => {
  const allItems = Object.values(items.itemsHashMap);
  let bgSum = 0;
  let hasBackground = false;
  for (const item of allItems) {
    if (item.isBackground) {
      hasBackground = true;
      bgSum += item.onPageEndTime - item.onPageStartTime;
    }
  }
  let baseDuration: number;
  if (hasBackground) {
    baseDuration = bgSum;
  } else {
    let maxItemEndTime = 0;
    for (const item of allItems) {
      if (item.onPageEndTime > maxItemEndTime) {
        maxItemEndTime = item.onPageEndTime;
      }
    }
    baseDuration = maxItemEndTime;
  }
  let maxAudioEnd = 0;
  if (audioClips) {
    for (const audio of Object.values(audioClips.audioItemsHashMap)) {
      const end = audio.onPageStartTime + getPlaybackDuration(audio.audioPlayer);
      if (end > maxAudioEnd) {
        maxAudioEnd = end;
      }
    }
  }
  let maxSubtitleEnd = 0;
  for (const item of allItems) {
    if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
      for (const subtitle of Object.values((item as TranscriptItemObject).subtitlesHashmap)) {
        maxSubtitleEnd = Math.max(maxSubtitleEnd, subtitle.endTime);
      }
    }
  }
  return combinePageDuration(baseDuration, maxAudioEnd, maxSubtitleEnd);
};

export const getPageDuration = (page: PageObject): number => {
  return computePageDuration(page.items, page.audioClips);
};
